'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Open a page at the top, on every navigation.
 *
 * THE BUG. Clicking through to a page could land you part-way down it. Nothing
 * on the destination moves the scroll — no scrollIntoView, no autoFocus, no
 * anchor — the App Router restores the previous position against a short
 * streaming placeholder, and then the real content grows underneath it. You end
 * up somewhere arbitrary, decided by how tall the PREVIOUS page was.
 *
 * This was already diagnosed and fixed once, as ScrollTopOnMount, and mounted
 * on the two results pages where it was first reported. Every other page kept
 * the bug — which is what "it happens on some other pages too" is.
 *
 * WHY IT IS NOT JUST THAT COMPONENT IN THE LAYOUT. The root layout PERSISTS
 * across client-side navigation, so a mount-only effect there would fire once
 * on first load and never again — exactly the case that needs it. This watches
 * the pathname instead.
 *
 * TWO THINGS IT MUST NOT BREAK, both deliberate places to land below the top:
 *
 *   A HASH is a request to go somewhere specific. The nav's Pro badge links to
 *   /settings#subscription so clicking your plan lands on billing management
 *   rather than the top of a general settings page.
 *
 *   BACK AND FORWARD should return you where you were. Sending someone to the
 *   top of a list they had scrolled halfway down is its own bug, so a popstate
 *   navigation is left alone and the browser's restoration is allowed to win.
 */
export default function ScrollTopOnNavigate() {
  const pathname = usePathname()
  const cameFromHistory = useRef(false)

  useEffect(() => {
    const onPop = () => {
      cameFromHistory.current = true
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Deep link — the hash owns the scroll position.
    if (window.location.hash) return
    // Back/forward — the browser owns it.
    if (cameFromHistory.current) {
      cameFromHistory.current = false
      return
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}
